import React, { useState, useMemo, useEffect } from "react";
import { Dialog, Typography, Card, CardContent, Avatar } from "@mui/material";
import { GetTeachersOutput } from "server/StartupAdmins CRUD/OutputsTypes";
import { getTeachers } from "server/StartupAdmins CRUD/Requests";
import { ApiResponse } from "server";
import { Search } from "lucide-react";

interface AssignTeachersModalProps {
  open: boolean;
  onClose: () => void;
  handleAssign: (
    submissionId: string,
    assignment: { teacher: string; controller: string },
  ) => void;
  submissionId: string | null;
}

const AssignTeachersModal: React.FC<AssignTeachersModalProps> = ({
  open,
  onClose,
  handleAssign,
  submissionId,
}) => {
  const [teachers, setTeachers] = useState<GetTeachersOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response: ApiResponse<GetTeachersOutput[]> = await getTeachers();
        if (response.status === 200 && Array.isArray(response.body)) {
          setError(null);
          setTeachers(response.body);
        } else {
          setError("Erreur lors de la récupération des enseignants.");
        }
      } catch (err) {
        setError("Une erreur s'est produite lors de la connexion au serveur.");
      } finally {
        setLoading(false);
      }
    };
    fetchTeachers();
  }, []);

  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [selectedController, setSelectedController] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");

  const filteredTeachers = useMemo(() => {
    return teachers.filter((teacher) => {
      const fullName = `${teacher.firstname} ${teacher.lastname}`.toLowerCase();
      return fullName.includes(searchQuery.toLowerCase());
    });
  }, [teachers, searchQuery]);

  const availableTeachers = useMemo(
    () =>
      filteredTeachers.filter((teacher) => teacher._id !== selectedController),
    [filteredTeachers, selectedController],
  );

  const availableControllers = useMemo(
    () => filteredTeachers.filter((teacher) => teacher._id !== selectedTeacher),
    [filteredTeachers, selectedTeacher],
  );

  const handleConfirm = () => {
    if (selectedTeacher && selectedController && submissionId) {
      handleAssign(submissionId, {
        teacher: selectedTeacher,
        controller: selectedController,
      });
      onClose();
    }
  };

  // Fonction pour générer les initiales
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSelect = (type: "teacher" | "controller", id: string) => {
    if (type === "teacher") {
      if (selectedController === id) {
        setSelectedController(null);
      }
      setSelectedTeacher(id === selectedTeacher ? null : id);
    } else {
      if (selectedTeacher === id) {
        setSelectedTeacher(null);
      }
      setSelectedController(id === selectedController ? null : id);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderTeacherCard = (
    teacher: GetTeachersOutput,
    type: "teacher" | "controller",
  ) => {
    const isSelected =
      type === "teacher"
        ? selectedTeacher === teacher._id
        : selectedController === teacher._id;

    return (
      <Card
        key={teacher._id}
        onClick={() => handleSelect(type, teacher._id)}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          mb: 1,
          p: 1,
          border: isSelected ? "2px solid #E3B505" : "1px solid #ddd",
          backgroundColor: isSelected ? "#FFFBEA" : "white",
        }}
      >
        <Avatar
          src={teacher.photoId}
          alt={teacher.firstname + " " + teacher.lastname}
          sx={{
            mr: 2,
            bgcolor: !teacher.photoId ? "#1976d2" : undefined,
          }}
        >
          {!teacher.photoId &&
            getInitials(teacher.firstname + " " + teacher.lastname)}
        </Avatar>
        <CardContent sx={{ flex: 1 }}>
          <Typography variant="body1" fontWeight="bold">
            {teacher.firstname + " " + teacher.lastname}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 9999 }}
    >
      <div className="bg-white rounded-xl shadow-xl flex flex-col max-h-[90vh] w-full">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b flex-shrink-0">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
            Assigner un correcteur et un contrôleur
          </h2>

          {/* Search Bar */}
          <div className="mt-3 sm:mt-4 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-8 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Rechercher un enseignant..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="p-3 sm:p-4 m-2 sm:m-4 bg-red-50 text-red-700 rounded-lg flex-shrink-0 text-sm sm:text-base">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-6 sm:p-8">
              <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-4 border-blue-500 border-t-transparent"></div>
            </div>
          ) : (
            <div className="p-3 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Teachers List */}
                <div className="flex flex-col">
                  <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-2 sm:mb-3 flex-shrink-0">
                    Correcteurs
                  </h3>
                  <div
                    className="overflow-y-auto pr-2 space-y-2"
                    style={{ maxHeight: "300px", minHeight: "200px" }}
                  >
                    {availableTeachers.length === 0 ? (
                      <div className="text-center text-gray-500 py-6 sm:py-8 text-sm sm:text-base">
                        Aucun correcteur disponible
                      </div>
                    ) : (
                      availableTeachers.map((teacher) => (
                        <div
                          key={teacher._id}
                          onClick={() =>
                            setSelectedTeacher(
                              teacher._id === selectedTeacher
                                ? null
                                : teacher._id,
                            )
                          }
                          className={`flex items-center p-2 sm:p-3 rounded-lg cursor-pointer transition-all ${
                            selectedTeacher === teacher._id
                              ? "bg-blue-50 border-2 border-blue-500"
                              : "border border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          <div
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white text-xs sm:text-sm ${
                              teacher.photoId ? "" : "bg-[#E3B500]"
                            }`}
                          >
                            {teacher.photoId ? (
                              <img
                                src={teacher.photoId}
                                alt={`${teacher.firstname} ${teacher.lastname}`}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              getInitials(
                                `${teacher.firstname} ${teacher.lastname}`,
                              )
                            )}
                          </div>
                          <div className="ml-2 sm:ml-4 flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate text-sm sm:text-base">
                              {teacher.firstname} {teacher.lastname}
                              <span
                                className={`inline-flex items-center px-1.5 sm:px-2.5 ml-2 sm:ml-3 py-0.5 rounded-full text-xs font-medium ${
                                  teacher.availability
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {teacher.availability.available
                                  ? "Disponible"
                                  : "Indisponible"}
                              </span>
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Controllers List */}
                <div className="flex flex-col">
                  <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-2 sm:mb-3 flex-shrink-0">
                    Contrôleurs
                  </h3>
                  <div
                    className="overflow-y-auto pr-2 space-y-2"
                    style={{ maxHeight: "300px", minHeight: "200px" }}
                  >
                    {availableControllers.length === 0 ? (
                      <div className="text-center text-gray-500 py-6 sm:py-8 text-sm sm:text-base">
                        Aucun contrôleur disponible
                      </div>
                    ) : (
                      availableControllers.map((teacher) => (
                        <div
                          key={teacher._id}
                          onClick={() =>
                            setSelectedController(
                              teacher._id === selectedController
                                ? null
                                : teacher._id,
                            )
                          }
                          className={`flex items-center p-2 sm:p-3 rounded-lg cursor-pointer transition-all ${
                            selectedController === teacher._id
                              ? "bg-blue-50 border-2 border-blue-500"
                              : "border border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          <div
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white text-xs sm:text-sm ${
                              teacher.photoId ? "" : "bg-[#E3B500]"
                            }`}
                          >
                            {teacher.photoId ? (
                              <img
                                src={teacher.photoId}
                                alt={`${teacher.firstname} ${teacher.lastname}`}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              getInitials(
                                `${teacher.firstname} ${teacher.lastname}`,
                              )
                            )}
                          </div>
                          <div className="ml-2 sm:ml-4 flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate text-sm sm:text-base">
                              {teacher.firstname} {teacher.lastname}
                              <span
                                className={`inline-flex items-center px-1.5 sm:px-2.5 ml-2 sm:ml-3 py-0.5 rounded-full text-xs font-medium ${
                                  teacher.availability
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {teacher.availability.available
                                  ? "Disponible"
                                  : "Indisponible"}
                              </span>
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-6 border-t bg-gray-50 rounded-b-xl flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm sm:text-base"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedTeacher || !selectedController}
            className="w-full sm:w-auto px-4 py-2 text-white bg-[#E3B500] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            Assigner
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default AssignTeachersModal;
